import React from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";
import { ReactComponent as PeopleSearchIcon } from "../../../../assets/images/People/people-search-16.svg";
import { ReactComponent as FilterChipCloseIcon } from "../../../../assets/images/People/filter-chip-close.svg";
import { DROPDOWN_HEIGHT, DROPDOWN_WIDTH } from "./customersConfig";
import { peopleSelectMenuProps } from "../peopleSelectStyles";

export { PeopleSearchIcon, FilterChipCloseIcon };

function SelectionTick() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none">
      <path
        d="M6.5 0.5L2.74922 4.25078L0.5 2.00031"
        stroke="#148DFD"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const checkboxIconWrapSx = {
  width: 16,
  height: 16,
  borderRadius: "4px",
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 0,
  flexShrink: 0,
  "& svg": { display: "block", flexShrink: 0 },
};

function CheckboxUncheckedIcon() {
  return (
    <Box
      component="span"
      sx={{
        ...checkboxIconWrapSx,
        border: "1px solid #D5DCE1",
        backgroundColor: "#FFF",
        boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
      }}
    />
  );
}

function CheckboxCheckedIcon() {
  return (
    <Box
      component="span"
      sx={{
        ...checkboxIconWrapSx,
        border: "1px solid #148DFD",
        backgroundColor: "#FFF",
        boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
      }}
    >
      <SelectionTick />
    </Box>
  );
}

export const FilterCheckbox = styled(Checkbox)(() => ({
  padding: 0,
  width: 16,
  height: 16,
  "&.MuiCheckbox-root": {
    color: "transparent",
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .MuiSvgIcon-root": {
    width: 16,
    height: 16,
    fontSize: 16,
    margin: 0,
  },
  "& > span": {
    display: "inline-flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",
    width: "16px !important",
    height: "16px !important",
    margin: "0 !important",
    lineHeight: 0,
  },
  "&:hover": { backgroundColor: "transparent" },
}));

export { CheckboxUncheckedIcon, CheckboxCheckedIcon };

export const dropdownPanelSx = {
  position: "absolute",
  top: "calc(100% + 8px)",
  left: 0,
  zIndex: 1000,
  width: DROPDOWN_WIDTH,
  height: DROPDOWN_HEIGHT,
  border: "1px solid #D5DCE1",
  borderRadius: "8px",
  padding: "8px",
  backgroundColor: "#FFFFFF",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
};

/** Cancels panel horizontal padding so option rows can paint edge-to-edge. */
export const dropdownOptionListSx = {
  flex: 1,
  mt: 1,
  mx: -1,
  overflowY: "auto",
  scrollbarWidth: "thin",
};

/** Full-width selected/hover row (matches Figma); content keeps 8px inset via px. */
export function dropdownOptionRowSx({ selected = false } = {}) {
  return {
    display: "flex",
    alignItems: "center",
    gap: 1,
    px: 1,
    py: 1,
    cursor: "pointer",
    backgroundColor: selected ? "#F9FAFA" : "transparent",
    "&:hover": { backgroundColor: "#F9FAFA" },
  };
}

/** MUI Select menu props aligned with Tags dropdown (224px height, scrollable list). */
export function customerEditSelectMenuProps({ openUpward = false } = {}) {
  const paperSx = {
    ...peopleSelectMenuProps.PaperProps.sx,
    maxHeight: DROPDOWN_HEIGHT,
    overflow: "hidden",
    boxSizing: "border-box",
    ...(openUpward ? { marginTop: 0, marginBottom: "8px" } : {}),
  };

  return {
    ...peopleSelectMenuProps,
    ...(openUpward
      ? {
          anchorOrigin: { vertical: "top", horizontal: "left" },
          transformOrigin: { vertical: "bottom", horizontal: "left" },
        }
      : {}),
    PaperProps: {
      ...peopleSelectMenuProps.PaperProps,
      sx: paperSx,
    },
    MenuListProps: {
      sx: {
        maxHeight: DROPDOWN_HEIGHT - 16,
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(0,0,0,0.2) transparent",
        padding: "4px 0",
      },
    },
  };
}

export const triggerSx = {
  borderRadius: "8px",
  border: "1px solid #D5DCE1",
  background: "#FFF",
  boxShadow: "0px 1px 2px rgba(6, 21, 34, 0.05)",
  height: "40px",
  padding: "0 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer",
  minWidth: 96,
  boxSizing: "border-box",
};

export const searchFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "8px",
    pl: "8px",
    pr: "8px",
    overflow: "visible",
    "&.Mui-focused fieldset": { border: "1px solid #3B82F6" },
  },
  "& fieldset": { border: "1px solid #D5DCE1" },
  "& input": {
    height: "40px",
    padding: "0 8px 0 0",
    fontSize: "14px",
    fontFamily: "Inter",
    fontWeight: 400,
    color: "#111827 !important",
    "&::placeholder": {
      color: "#C5CED6",
      opacity: 1,
      fontSize: "14px",
      fontFamily: "Inter",
    },
  },
  "& .MuiInputAdornment-root": {
    marginRight: "8px",
    marginLeft: 0,
    maxHeight: "unset",
    overflow: "visible",
  },
  "& .MuiInputAdornment-root svg": {
    display: "block",
    overflow: "visible",
    flexShrink: 0,
  },
};
